
































































































import rest from "@/rest";
import { Component, Prop, Vue, Watch, Ref } from "vue-property-decorator";
import { namespace } from "vuex-class";
import { ElementSaveStateVm, ExChoiceList } from "@/interfaces/ExDataSet";
import MarkdownViewer from "../Shared/MarkdownViewer.vue";

const globals = namespace("globals");
const quizplayerstore = namespace("quizplayerstore");

@Component({
  components: {
    MarkdownViewer
  }
})
export default class CompSequentialList extends Vue {
  @Prop() guid!: string;
  @Prop() choiceList!: ExChoiceList;
  @Prop() finalized!: boolean;
  @Prop() compSettings!: string;

  @Prop({ default: false }) private preventJumping!: boolean; // Used by settings component

  @quizplayerstore.Action setElementState!: (saveState: ElementSaveStateVm) => void;
  @quizplayerstore.Action getElementState!: (settings: string) => Promise<string | null>;

  expandedPanel: number | null = null;

  // Data properties
  private settings: any = null;

  // Lifecycle hooks
  async mounted(): Promise<void> {
    // Parse component settings
    if (this.compSettings) {
      this.settings = JSON.parse(this.compSettings);
    }

    // Try to load existing state for this element
    if (this.choiceList?.items) {
      let saveState = await this.getElementState(this.guid);
      if (saveState) {
        // Apply the loaded settings
        const selectedIds = JSON.parse(saveState);
        this.choiceList.items?.forEach(item => {
          item.selected = selectedIds.includes(item.id);
        });
      }
    }
  }

  beforeDestroy(): void {
  }

  private onListElementChanged() {
    if (this.finalized || !this.choiceList || !this.getPreventJumping) return;

      // Create an array with all ids of selected choices
      const selectedIds = this.choiceList.items?.filter(item => item.selected).map(item => item.id) ?? [];
      // console.log("Selected IDs:", selectedIds);

      this.setElementState( {
          guid: this.guid,
          settings: JSON.stringify(selectedIds)
        } as ElementSaveStateVm
      );

    // Enable "Weiter" button if preventJumping is active
    const listItemCount = this.choiceList.items?.length ?? 0;
    const selectedCount = this.choiceList.items?.filter(item => item.selected).length ?? 0;
    if (selectedCount == listItemCount) {
      this.$store.commit("quizplayerstore/setChoiceState",
        { id: this.choiceList.id, questionId: this.choiceList.questionId, isRight: true, selectionLable: "" }
      );
    }
  }

  get getPreventJumping() {
    if (this.settings?.preventJumping)
      return this.settings.preventJumping;

    return this.preventJumping;
  }

  panelStyle(index: number) {
    const dark = (this as any).$vuetify && (this as any).$vuetify.theme && (this as any).$vuetify.theme.dark;
    // base backgrounds
    // let bg = dark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.03)";
    let bg = "rgba(255,255,255,0.2)";

    // If panel is disabled in light mode, make it even lighter
    if (!dark && this.isPanelDisabled(index)) {
      bg = "rgba(0,0,0,0.01)";
    }

    // If active panel, slightly stronger background
    if (this.expandedPanel === index) {
      bg = dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)";
    }

    return {
      background: bg,
      borderRadius: "6px",
      marginBottom: "8px",
      padding: "4px 8px"
    } as any;
  }

  onPanelChange(val: number | null) {
    if (val === null) return;
    const idx = val as number;
    const items = this.choiceList?.items;
    if (items && items[idx]) {
      (this as any).$set(items[idx], "selected", true);
    }
    this.onListElementChanged();
  }

  isPanelDisabled(index: number) {
    // If preventJumping is disabled, allow all panels normally
    if (!this.getPreventJumping) return false;

    const items = this.choiceList?.items;
    // first panel always enabled
    if (index === 0) return false;
    // allow closing currently opened panel even if predecessor not selected
    if (this.expandedPanel === index) return false;
    return !(items && items[index - 1] && items[index - 1].selected);
  }

  isActive(index: number) {
    return this.expandedPanel === index;
  }

  chipColor(index: number) {
    const dark = (this as any).$vuetify && (this as any).$vuetify.theme && (this as any).$vuetify.theme.dark;
    if (this.isActive(index)) return 'primary';
    if (this.isPanelDisabled(index)) return dark ? 'grey darken-3' : 'white darken-1';
    return dark ? 'white' : 'grey darken-4'; // 'primary';
  }

  chipClass(index: number) {
    const dark = (this as any).$vuetify && (this as any).$vuetify.theme && (this as any).$vuetify.theme.dark;
    if (this.isPanelDisabled(index) && dark) return 'white--text';
    return '';
  }

  get title() {
    return this.settings?.title || "";
  }

  get srStatusMessage(): string {
    const items = this.choiceList?.items;
    if (!items || items.length === 0) return "";

    const totalCount = items.length;
    const viewedCount = items.filter(i => i.selected).length;

    if (this.getPreventJumping && viewedCount === totalCount) {
      return `Alle ${totalCount} Elemente wurden angesehen. Sie können jetzt fortfahren.`;
    }

    if (this.expandedPanel !== null && this.expandedPanel !== undefined) {
      const idx = this.expandedPanel as number;
      const item = items[idx];
      if (!item) return "";

      let msg = `Element ${idx + 1} von ${totalCount} geöffnet: ${item.label}.`;
      if (this.getPreventJumping && viewedCount < totalCount) {
        msg += ` ${viewedCount} von ${totalCount} Elementen angesehen.`;
      }
      return msg;
    }

    return "";
  }

  panelAriaLabel(index: number): string {
    const items = this.choiceList?.items;
    const totalCount = items?.length ?? 0;
    const item = items?.[index];
    if (!item) return "";

    let label = `Element ${index + 1} von ${totalCount}: ${item.label}.`;

    if (this.isPanelDisabled(index)) {
      label += " Gesperrt – öffnen Sie zuerst das vorherige Element.";
    } else if (item.selected) {
      label += " Bereits angesehen.";
    } else {
      label += " Drücken Sie die Eingabetaste oder Leertaste, um zu öffnen.";
    }

    return label;
  }

  handleKeyNavigation(event: KeyboardEvent, currentIndex: number): void {
    const items = this.choiceList?.items;
    if (!items || items.length === 0) return;

    let targetIndex = -1;

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        event.preventDefault();
        targetIndex = currentIndex + 1;
        if (targetIndex >= items.length) targetIndex = 0;
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        event.preventDefault();
        targetIndex = currentIndex - 1;
        if (targetIndex < 0) targetIndex = items.length - 1;
        break;
      case 'Home':
        event.preventDefault();
        targetIndex = 0;
        break;
      case 'End':
        event.preventDefault();
        targetIndex = items.length - 1;
        break;
      default:
        return;
    }

    this.focusPanelItem(targetIndex);
  }

  focusPanelItem(index: number): void {
    const headers = this.$el.querySelectorAll<HTMLElement>('.v-expansion-panel-header');
    const el = headers[index];
    if (el && !(el as HTMLButtonElement).disabled) {
      el.focus();
    }
  }
}
